import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

export interface WidgetPosition {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

const STORAGE_KEY = 'dashboardWidgetPositions';
const MAX_WIDGETS = 12;

@Injectable({
  providedIn: 'root',
})
export class DashboardFacadeService {
  private _widgetPositions = new BehaviorSubject<WidgetPosition[]>([]);
  widgetPositions$ = this._widgetPositions.asObservable();

  constructor() {
    this.loadWidgetPositions();
  }

  private loadWidgetPositions(): void {
    try {
      const storedPositions = localStorage.getItem(STORAGE_KEY);
      if (storedPositions) {
        const parsedPositions: WidgetPosition[] = JSON.parse(storedPositions);

        if (Array.isArray(parsedPositions) && this.isValidWidgetPositionsArray(parsedPositions)) {
          this._widgetPositions.next(parsedPositions);
        } else {
          console.warn('Invalid widget positions data found, resetting');
          this.resetWidgetPositions();
        }
      }
    } catch (error) {
      console.error('Error loading widget positions:', error);
      this.resetWidgetPositions();
    }
  }

  private isValidWidgetPositionsArray(positions: any[]): boolean {
    return positions.every(
      pos =>
        typeof pos === 'object' &&
        pos !== null &&
        typeof pos.id === 'string' &&
        typeof pos.x === 'number' &&
        typeof pos.y === 'number' &&
        typeof pos.w === 'number' &&
        typeof pos.h === 'number',
    );
  }

  private saveWidgetPositions(positions: WidgetPosition[]): void {
    try {
      const limitedPositions = positions.slice(-MAX_WIDGETS);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedPositions));

      const savedData = localStorage.getItem(STORAGE_KEY);
      if (!savedData) {
        throw new Error('Failed to save widget positions');
      }
    } catch (error) {
      console.error('Error saving widget positions:', error);

      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('Storage quota exceeded, trying to save fewer items');
        try {
          const reducedPositions = positions.slice(-Math.floor(MAX_WIDGETS / 2));
          localStorage.setItem(STORAGE_KEY, JSON.stringify(reducedPositions));
          this._widgetPositions.next(reducedPositions);
        } catch {
          this.resetWidgetPositions();
        }
      }
    }
  }

  updateWidgetPosition(widget: WidgetPosition): void {
    const currentPositions = this._widgetPositions.value;
    const existingIndex = currentPositions.findIndex(w => w.id === widget.id);

    let updatedPositions: WidgetPosition[];
    if (existingIndex !== -1) {
      updatedPositions = [
        ...currentPositions.slice(0, existingIndex),
        widget,
        ...currentPositions.slice(existingIndex + 1),
      ];
    } else {
      updatedPositions = [...currentPositions, widget];
    }

    this._widgetPositions.next(updatedPositions);
    this.saveWidgetPositions(updatedPositions);
  }

  removeWidgetPosition(id: string): void {
    const currentPositions = this._widgetPositions.value;
    const updatedPositions = currentPositions.filter(w => w.id !== id);

    this._widgetPositions.next(updatedPositions);
    this.saveWidgetPositions(updatedPositions);
  }

  resetWidgetPositions(): void {
    localStorage.removeItem(STORAGE_KEY);
    this._widgetPositions.next([]);
  }

  validateAndCleanPositions(): void {
    const currentPositions = this._widgetPositions.value;

    const uniqueIds = new Set<string>();
    const cleanedPositions = currentPositions.filter(widget => {
      if (uniqueIds.has(widget.id)) {
        return false;
      }
      uniqueIds.add(widget.id);
      return true;
    });

    if (cleanedPositions.length !== currentPositions.length) {
      console.warn(
        `Removed ${currentPositions.length - cleanedPositions.length} duplicate widget positions`,
      );
      this._widgetPositions.next(cleanedPositions);
      this.saveWidgetPositions(cleanedPositions);
    }
  }
}
